/**
 * Global playback coordinator.
 *
 * Enforces the invariant: at most one PlayableItem is "active" across every
 * player surface (immersive feed, inline brief preview, future surfaces) at
 * any given time. The coordinator owns *state* — `activeId`, `isMuted`,
 * `onEnded` subscriptions — not playback APIs. Surfaces are responsible
 * for translating state into mount/unmount: an inactive slide renders as
 * a poster (no iframe), and "switching active" stops the previous embed
 * by tearing it down. A future provider bridge (YouTube IFrame API,
 * Spotify Embed API) can pause/setMuted in place by subscribing to the
 * coordinator and reacting to `activeId` changes.
 *
 * Surfaces drive navigation by calling {@link setActive}. They don't share a
 * queue — each surface interprets what "next" means for itself, subscribes
 * to {@link onEnded} for its current item, and calls {@link setActive} again.
 *
 * Not a Zustand store on purpose: this is a singleton process-wide invariant,
 * not component-scoped state. Using React's `useSyncExternalStore` gives us
 * concurrent-safe subscription without pulling in a dependency.
 *
 * Cross-tab coordination intentionally absent. Two tabs in the same browser
 * may both hold an active slot; this is product-level expectation, not a
 * bug. If we ever need it, BroadcastChannel API is the lever.
 */

export interface PlayerState {
  activeId: string | null
  isMuted: boolean
}

type Listener = () => void
type EndedCallback = () => void

class PlaybackCoordinator {
  private state: PlayerState = { activeId: null, isMuted: true }
  private listeners = new Set<Listener>()
  private endedCallbacks = new Map<string, Set<EndedCallback>>()

  subscribe = (fn: Listener): (() => void) => {
    this.listeners.add(fn)
    return () => {
      this.listeners.delete(fn)
    }
  }

  getSnapshot = (): PlayerState => this.state

  setActive(id: string | null): void {
    if (this.state.activeId === id) return
    this.setState({ activeId: id })
  }

  setMuted(muted: boolean): void {
    if (this.state.isMuted === muted) return
    this.setState({ isMuted: muted })
  }

  toggleMuted(): void {
    this.setMuted(!this.state.isMuted)
  }

  /**
   * Subscribe to end-of-track for a specific item. Returns an unsubscribe.
   * Surfaces use this to implement auto-advance.
   */
  onEnded(id: string, cb: EndedCallback): () => void {
    let set = this.endedCallbacks.get(id)
    if (!set) {
      set = new Set()
      this.endedCallbacks.set(id, set)
    }
    set.add(cb)
    return () => {
      this.endedCallbacks.get(id)?.delete(cb)
    }
  }

  /** Called by bridges (or by shells via duration timer fallback). */
  emitEnded(id: string): void {
    this.endedCallbacks.get(id)?.forEach((cb) => cb())
  }

  private setState(patch: Partial<PlayerState>): void {
    this.state = { ...this.state, ...patch }
    this.listeners.forEach((fn) => fn())
  }
}

export const coordinator = new PlaybackCoordinator()
